import Snackbar from "src/base-components/Snackbar";
import ReminderToast from "src/components/ReminderToast";
import GlobalReminderModals from "src/components/GlobalReminderModals";
import NotificationPermissionModal from "src/components/NotificationPermissionModal";
import Router from "./router";
import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "./stores";
import { selectAuthUser } from "./stores/auth";
import { appInitializer } from "./helpers";
import {
  consumePendingNotificationTarget,
  markSessionActive,
} from "./helpers/session";
// import { usePushNotification } from "./firebase";
import runOneSignal, { usePushNotification } from "./onesignal";
import CallsLogsModal from "./components/CallsLogsModal";
import PwaInstallPrompt from "./components/PwaInstallPrompt";
import ThemeCustomizer from "./components/ThemeCustomizer";
import { checkAndPromptForNotificationPermissions } from "./utils/notificationPermissionUtils";


const AppContent = () => {
  const pushNotification = usePushNotification();
  const authUser = useAppSelector(selectAuthUser);
  const navigate = useNavigate();

  useEffect(() => {
    const openNotificationTarget = (path?: string | null) => {
      if (!path || path === "/login" || path === "/password-reset") {
        return;
      }
      navigate(path);
    };

    const pendingPath = consumePendingNotificationTarget();
    openNotificationTarget(pendingPath);

    const handleNotificationOpen = (event: Event) => {
      const path = (event as CustomEvent)?.detail?.path;
      openNotificationTarget(path);
    };

    window.addEventListener(
      "notification-open",
      handleNotificationOpen as EventListener,
    );
    return () => {
      window.removeEventListener(
        "notification-open",
        handleNotificationOpen as EventListener,
      );
    };
  }, [navigate]);

  useEffect(() => {
    if (!authUser?.token) {
      return;
    }

    markSessionActive();
    const heartbeat = window.setInterval(() => {
      markSessionActive();
    }, 30000);

    return () => {
      window.clearInterval(heartbeat);
    };
  }, [authUser?.token]);

  useEffect(() => {
    const initializeApp = async () => {
      await runOneSignal();
      if (authUser?.token) {
        setTimeout(() => {
          checkAndPromptForNotificationPermissions();
        }, 2000); 
      }

      appInitializer(authUser);
    };

    const handleShowModal = () => {
      checkAndPromptForNotificationPermissions();
    };

    window.addEventListener(
      "show-notification-permission-modal",
      handleShowModal
    );

    initializeApp();
    pushNotification;

    // Expose a dev helper to trigger the reminder toast from console
    try { 
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      window.showReminderToastForDebug = (payload: any) =>
        window.dispatchEvent(
          new CustomEvent("show-reminder-toast", { detail: payload })
        );

      // Expose helper to manually trigger notification permission modal
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      window.showNotificationPermissionModal = () =>
        window.dispatchEvent(
          new CustomEvent("show-notification-permission-modal")
        );
    } catch (err) {
      // ignore
    }

    // Cleanup event listener
    return () => {
      window.removeEventListener(
        "show-notification-permission-modal",
        handleShowModal
      );
    };
  }, [authUser?.token]); // Add authUser.token as dependency

  const RouterCallback = useCallback(
    () => <Router />,
    [authUser?.role?.permissions?.length]
  );

  return (
    <>
      <PwaInstallPrompt />
      <ThemeCustomizer />
      <Snackbar />
      <ReminderToast />
      <CallsLogsModal />
      <GlobalReminderModals />
      <NotificationPermissionModal />
      <RouterCallback />
    </>
  );
};
export default AppContent;
